import React, { useState, useEffect } from 'react';
import { StreamingService } from '../services/streaming';

const RecommendationFeed = ({ limit = 20 }) => {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReasoning, setShowReasoning] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    loadRecommendations();
  }, [limit]);

  const loadRecommendations = async () => {
    setLoading(true);
    try {
      const data = await StreamingService.getRecommendations(limit, showReasoning);
      setRecommendations(data);
    } catch (error) {
      console.error('Error loading recommendations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRating = async (videoId, rating) => {
    try {
      await StreamingService.rateVideo(videoId, rating);
      alert('Rating saved!');
      loadRecommendations(); // Refresh recommendations
    } catch (error) {
      alert('Failed to save rating');
    }
  };

  const handleAddToWatchlist = async (videoId) => {
    try {
      await StreamingService.addToWatchlist(videoId);
      alert('Added to watchlist!');
    } catch (error) {
      alert('Failed to add to watchlist');
    }
  };

  const showExplanation = async (videoId) => {
    try {
      const explanation = await StreamingService.explainRecommendation(videoId);
      setSelectedVideo(explanation);
    } catch (error) {
      console.error('Error loading explanation:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-2xl text-white">Loading recommendations...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-5xl font-bold mb-2 bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
            Recommended for You
          </h1>
          <p className="text-gray-400">Powered by AI • Personalized just for you</p>
        </div>
        <button
          onClick={() => {
            setShowReasoning(!showReasoning);
            loadRecommendations();
          }}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg font-bold transition-all"
        >
          {showReasoning ? 'Hide' : 'Show'} AI Reasoning
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {recommendations.map((video, index) => (
          <div
            key={video.id || index}
            className="bg-gray-900/50 rounded-lg overflow-hidden border border-gray-700 hover:border-purple-500 transition-all transform hover:scale-105 cursor-pointer"
          >
            {/* Thumbnail */}
            <div className="relative aspect-video bg-gradient-to-br from-purple-900/50 to-pink-900/50">
              {video.thumbnail_url ? (
                <img
                  src={video.thumbnail_url}
                  alt={video.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-6xl">
                  🎬
                </div>
              )}
              {video.duration && (
                <div className="absolute bottom-2 right-2 bg-black/80 px-2 py-1 rounded text-xs font-bold">
                  {Math.floor(video.duration / 60)}:{String(video.duration % 60).padStart(2, '0')}
                </div>
              )}
            </div>

            {/* Content */}
            <div className="p-4">
              <h3 className="text-white font-bold text-lg mb-2 line-clamp-2">
                {video.title}
              </h3>
              <p className="text-gray-400 text-sm mb-3 line-clamp-2">
                {video.description}
              </p>

              {/* Category & Tags */}
              <div className="flex flex-wrap gap-2 mb-3">
                <span className="px-2 py-1 bg-purple-600/30 text-purple-300 rounded text-xs">
                  {video.category}
                </span>
                {video.tags && video.tags.slice(0, 2).map((tag, i) => (
                  <span key={i} className="px-2 py-1 bg-gray-800 text-gray-400 rounded text-xs">
                    {tag}
                  </span>
                ))}
              </div>

              {/* Stats */}
              <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                <span>⭐ {video.rating ? video.rating.toFixed(1) : 'N/A'}</span>
                <span>👁️ {video.views ? video.views.toLocaleString() : 0} views</span>
              </div>

              {/* AI Reasoning */}
              {showReasoning && video.reasoning && (
                <div className="mb-3 p-3 bg-purple-900/20 rounded-lg border border-purple-500/30">
                  <div className="text-xs text-purple-300 font-bold mb-2">🤖 Why we recommend this:</div>
                  {video.reasoning.map((reason, i) => (
                    <div key={i} className="text-xs text-gray-400 mb-1">
                      • {reason}
                    </div>
                  ))}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleAddToWatchlist(video.id)}
                  className="flex-1 py-2 bg-purple-600 hover:bg-purple-700 rounded font-bold text-sm transition-all"
                >
                  + Watchlist
                </button>
                <button
                  onClick={() => showExplanation(video.id)}
                  className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded font-bold text-sm transition-all"
                  title="Why this recommendation?"
                >
                  ❓
                </button>
              </div>

              {/* Rating Stars */}
              <div className="mt-3 flex justify-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => handleRating(video.id, star)}
                    className="text-2xl hover:scale-125 transition-transform"
                  >
                    {star <= (video.rating || 0) ? '⭐' : '☆'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Explanation Modal */}
      {selectedVideo && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedVideo(null)}
        >
          <div
            className="bg-gray-900 p-8 rounded-2xl max-w-2xl w-full border-2 border-purple-500"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-3xl font-bold mb-4 text-purple-400">
              Why we recommended this
            </h2>
            <h3 className="text-2xl font-bold text-white mb-6">
              {selectedVideo.video_title}
            </h3>

            <div className="space-y-4 mb-6">
              {selectedVideo.reasons && selectedVideo.reasons.map((reason, index) => (
                <div key={index} className="bg-gray-800 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-purple-400 font-bold capitalize">
                      {reason.reason.replace('_', ' ')}
                    </span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 bg-gray-700 rounded-full h-2">
                        <div
                          className="h-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
                          style={{ width: `${reason.confidence * 100}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-400">
                        {(reason.confidence * 100).toFixed(0)}%
                      </span>
                    </div>
                  </div>
                  <p className="text-gray-300 text-sm">{reason.description}</p>
                </div>
              ))}
            </div>

            <button
              onClick={() => setSelectedVideo(null)}
              className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg font-bold hover:shadow-lg transition-all"
            >
              Got it!
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecommendationFeed;